import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { LinkButton } from 'components/buttons';
import { DataDisplayTable } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { ShowHide } from 'components/layout';
import { BulletedList } from 'components/listing';
import { NotificationInline } from 'components/notifications';
import { Tooltip } from 'components/tooltip';
import { hasRaisingTriggers, OutcomeLevelDisplay, triggerShape, TriggersMet } from 'pages/employee-reviews/shared';

const defaultArray = [];
const defaultFunction = () => {};
const dateFormat = "DD MMM YYYY";

// The Introduction step of the complete-review wizard: what the review covers, the triggers that
// raised it, and the employee's previous review of this type, for the manager to go over before
// starting. The review type, due date and who raised it are in the page header above every step,
// so they are not repeated here.
//
// previousReviewId is null when the employee has no completed review of this type. Otherwise the
// review is read separately from the wizard's own load: previousReview holds it once it has loaded,
// and previousReviewFailed reports that it could not be.
export function IntroductionStep(
    {
        topicTitles = defaultArray,
        raisedBy = null,
        triggers = defaultArray,
        triggerNoticeDismissed = false,
        onTriggerNoticeDismiss = defaultFunction,
        previousReviewId = null,
        previousReview = null,
        previousReviewFailed = false,
        loading = false
    }
) {

    const previousReviewLoaded = previousReviewId != null && !previousReviewFailed && !loading && previousReview != null;

    function renderPreviousReview() {
        if (previousReviewId == null) {
            return (<p className={classNames({ "bp6-skeleton": loading })}>No previous review</p>);
        }

        if (previousReviewFailed) {
            return (<p>The previous review could not be loaded.</p>);
        }

        return (
            <DataDisplayTable loading={!previousReviewLoaded} displayInRows={false} columnsPerRow={4} data={[
                { key: "previous-completed", header: "Completed", value: <DateToLocal format={dateFormat}>{previousReview?.completedDate}</DateToLocal> },
                { key: "previous-completed-by", header: "Completed By", value: previousReview?.completedBy },
                { key: "previous-outcome", header: "Outcome", value: <OutcomeLevelDisplay outcomeLevel={previousReview?.outcomeLevel} /> }
            ]} />
        );
    }

    return (
        <div>
            <h2>Introduction</h2>
            <DataDisplayTable loading={loading} displayInRows={false} data={[
                {
                    key: "topics",
                    header: "Topics to be reviewed",
                    value: <BulletedList listItems={topicTitles} loading={loading} />,
                    fullWidth: true
                }
            ]} />
            <ShowHide
                evaluator={hasRaisingTriggers(raisedBy, triggers) && !triggerNoticeDismissed}
                show={(
                    <NotificationInline
                        show
                        onClose={onTriggerNoticeDismiss}
                        text={<TriggersMet triggers={triggers} leadIn="This review was raised because" referToFollowing />}
                        intent="primary"
                    />
                )}
            />
            <div className="inline-items spacer-bottom-small">
                <h3 className={classNames({ "bp6-skeleton": loading })}>Previous review</h3>
                <ShowHide
                    evaluator={previousReviewLoaded}
                    show={(
                        // Opens in a new tab so the manager keeps their place in the wizard.
                        <Tooltip content="Open the previous review">
                            <LinkButton
                                icon="share"
                                iconOnly
                                minimal
                                large={false}
                                intent="primary"
                                href={`/employee-reviews/completed-reviews/${previousReviewId}`}
                                openInNewTab
                            />
                        </Tooltip>
                    )}
                />
            </div>
            {renderPreviousReview()}
        </div>
    );
}

IntroductionStep.propTypes = {
    topicTitles: PropTypes.arrayOf(PropTypes.string),
    raisedBy: PropTypes.string,
    triggers: PropTypes.arrayOf(triggerShape),
    triggerNoticeDismissed: PropTypes.bool,
    onTriggerNoticeDismiss: PropTypes.func,
    previousReviewId: PropTypes.number,
    previousReview: PropTypes.object,
    previousReviewFailed: PropTypes.bool,
    loading: PropTypes.bool
};
